/* eslint-disable react-refresh/only-export-components -- the drawer and the helpers its callers share */
import { useId, useState, type ReactNode } from 'react'
import { LEDGER_CLASS_CHIPS, type LedgerChannel } from '@marva/shared/ledger-vocabulary'
import { SHADOW_IGNORED_CHIP } from '@marva/shared/reply-vocabulary'
import {
  type Activity,
  type CampaignDecisionRow,
  type LedgerCampaignDetail,
  type LedgerCampaignMember,
  type LedgerMerchantActionDetail,
  type LedgerReplyDetail,
  type ProposalAnswerAfter,
  type ProposalAuthorityState,
  type LedgerReplySendState,
  type SkipReason,
  type ActivityFeedback,
} from '../../services/api'
import DecisionFeedback, { type FeedbackState } from './DecisionFeedback'
import {
  actionState,
  campaignRowHold,
  campaignRowPill,
  campaignRowReasoning,
  campaignRowReceipt,
  contentLabel,
  fmtCurrency,
  narrative,
  outcomeLabel,
  scheduleLinePrefix,
  visibleReasoning,
  typeLabel,
  type DisplayActivity,
} from '../../lib/activityNarrative'
import { rungName } from '../setup/rungCopy'
import { answerOutcome } from '../chat/answerOutcome'
import {
  Button,
  Card,
  EntryList,
  EntryRow,
  Chip,
  ChipGroup,
  Drawer,
  Hint,
  Icon,
  KeyValue,
  LoadingState,
  MessageBody,
  Meta,
  Reading,
  ReadingSection,
  Stat,
  TileGrid,
  Timeline,
  Transcript,
} from '../../ds'

type ChipTone = 'neutral' | 'gold' | 'sage' | 'danger'

/**
 * The decision drawer — the Ledger's Reading drawer (D-36: `Drawer size="lg"` › `Reading`
 * sections). Extracted from the retired `screens/Journal.tsx` (MAR-110); the Journal's own
 * variant went with it (MAR-372), so this is the one rendering of a decision's story.
 */
export function fmtTime(ts?: string) {
  if (!ts) return ''
  return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}
export function fmtWhen(ts: string) {
  return new Date(ts).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + ' · ' + fmtTime(ts)
}
/**
 * A timeline row's when: the day and the time together, in the Timeline's own (mono) column, so a
 * row never carries its time twice in two fonts (MAR-412). Wraps at the space in the narrow column.
 */
function fmtStamp(ts: string) {
  return new Date(ts).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + ' ' + fmtTime(ts)
}
// Events worth showing in the drawer's outcome timeline (send → open → click →
// conversion); behavioral noise (page views etc.) stays out.
const TIMELINE_EVENTS = new Set([
  'email_sent', 'sms_sent', 'chat_message_sent', 'popup_displayed',
  'email_delivered', 'sms_delivered',
  'email_opened', 'email_clicked', 'sms_clicked',
  'conversion', 'converted',
])
// A campaign's `skip`-mirrored row, by its TYPED key (MAR-64). The feed derives the key from
// the receipt class — never from the agent's prose, which is how a wait that mentioned an
// unsubscribe used to be labelled "Unsubscribed". A `Record` over the key union, so a new class
// fails to compile until it has a label here.
const SKIP_REASON_LABEL: Record<SkipReason, string> = {
  agent_judgment: 'Chose to wait',
  escalate: 'Needs your input',
  decision_failed: 'Fault on our side',
  legacy_ambiguous: 'Not sent (older record)',
  execution_failed: 'Didn’t go through',
  help_blocked: 'Held back',
  help_withdrawn: 'Withdrawn',
  help_deferred: 'Scheduled',
  help_shadowed: 'Held for review',
  help_executed: 'Sent',
  help_in_flight: 'Sending',
}
// A key an older feed sent (`recent_communication`, `other`…) was regexed out of prose, so it
// is not repeated to the merchant as if it were a reason.
function skipReasonLabel(key?: string): string {
  return key && key in SKIP_REASON_LABEL ? SKIP_REASON_LABEL[key as SkipReason] : 'Not sent'
}

// The drawer's headline for an entry. Module-level because the Ask-entity
// publisher above needs the same string: the dock's pin banner has to name the
// entry exactly as the drawer titles it, and two copies would drift.
// Aggregated campaigns lead with the sale/product name, not a generic label.
export function storyTitle(a: DisplayActivity): string {
  const c = a.content
  return a.metadata?.isAggregated
    ? c?.groupName || a.title
    : c?.subject || (a.title && a.title !== a.reasoning ? a.title : typeLabel(a.type))
}

/**
 * Channel labels for the Ledger's rows and its reply drawer. The vocabulary owns the channel
 * WORDS; a `Record` over its type makes a new channel a compile error until it has a label.
 */
export const LEDGER_CHANNEL_LABELS: Record<LedgerChannel, string> = {
  chat: 'Chat',
  email: 'Email',
  sms: 'SMS',
  popup: 'On site',
  contact_capture: 'Contact form',
  reaction_prompt: 'Reaction prompt',
}
/** A channel token in the merchant's words; an unknown token falls back to itself. */
export function channelLabel(channel?: string | null): string | null {
  if (!channel) return null
  return (LEDGER_CHANNEL_LABELS as Record<string, string>)[channel] ?? channel
}

/**
 * Chips drawn gold — read from the vocabularies, not restated: an escalation, a hold, and a reply
 * that went out while the workspace was in Shadow (these paths don't honour it, and say so).
 */
const GOLD_CHIPS = new Set(
  [LEDGER_CLASS_CHIPS.escalate, LEDGER_CLASS_CHIPS.help_shadowed, SHADOW_IGNORED_CHIP].filter(Boolean)
)
export function isGoldChip(chip?: string | null): boolean {
  return !!chip && GOLD_CHIPS.has(chip)
}

/** A reply that went out more than once says so — the honest duplicate, not a tidied count. */
export function sentTimes(n?: number | null): string | null {
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 2) return null
  return n === 2 ? 'sent twice' : `sent ${n} times`
}

/** What happened to a reply's send, in the merchant's words — as the producer stated it. */
const REPLY_SEND_STATE_WORDS: Record<LedgerReplySendState, string> = {
  sent: 'Sent.',
  handed_off: 'Handed to the carrier — nothing back yet to say it arrived.',
  unknown: 'Outcome unknown — she can’t say for sure whether it went.',
  failed: 'Didn’t send.',
  withheld_shadow: 'Held — not sent, because you were in Shadow.',
  withheld_provenance: 'Held — not sent. Her draft said your team had answered, and no answer from you was on file.',
  withheld_disabled: 'Held — not sent, because this workspace is disabled.',
  // MAR-159 review: SMS switched off, the number opted out, the text budget spent, or quiet hours
  // for a reply that was not an immediate answer. The Ledger line names which.
  withheld_compliance: 'Held — not sent, because a limit you set applied.',
}

/** A detail that is a reply record (`rep_…`), not a decision. */
export function isReplyDetail(a: Activity | null | undefined): a is LedgerReplyDetail {
  return !!a && a.source === 'reply'
}

/**
 * An applied merchant-action receipt (`act_…`, MAR-175) — the merchant's own change, not a
 * judgment. Matches the ledger ROW as well as the detail, so a row standing in while the detail
 * is read already draws as a change, never as a decision with a why and a feedback control.
 */
export function isMerchantAction(a: unknown): boolean {
  return !!a && typeof a === 'object' && (a as { source?: unknown }).source === 'merchant_action'
}

const isAuthorityState = (v: unknown): v is ProposalAuthorityState =>
  !!v && typeof v === 'object' && typeof (v as ProposalAuthorityState).rung === 'string'
const isAnswerAfter = (v: unknown): v is ProposalAnswerAfter =>
  !!v && typeof v === 'object' && typeof (v as ProposalAnswerAfter).answerText === 'string'
const exceptionCount = (s: ProposalAuthorityState | null) => (s && Array.isArray(s.exceptions) ? s.exceptions.length : 0)

/**
 * A merchant-action receipt's drawer body (MAR-175): exactly what changed, in the words of the
 * screen that owns it. No why (it was the merchant's call) and no feedback (nothing to rate).
 * A `customer_answer` receipt holds no question, so none is shown — only the answer, and what its
 * return did (`result.returnStatus`, in the same words as the Ask receipt and the Inbox), so the
 * receipt never implies an answer reached a customer it did not.
 */
function MerchantActionBody({ m }: { m: Partial<LedgerMerchantActionDetail> }) {
  if (m.capability === 'authority_change') {
    const before = isAuthorityState(m.before) ? m.before : null
    const after = isAuthorityState(m.after) ? m.after : null
    if (!after) return null
    const b = exceptionCount(before)
    const n = exceptionCount(after)
    return (
      <ReadingSection label="What changed">
        <KeyValue
          items={[
            {
              label: 'Rung',
              value: (
                <span data-testid="action-rung">
                  {before ? rungName(before.rung) : 'Not set'}
                  {' → '}
                  {rungName(after.rung)}
                </span>
              ),
            },
            {
              label: 'Exceptions',
              value: <span data-testid="action-exceptions">{b === n ? `${n}, unchanged` : `${b} → ${n}`}</span>,
            },
          ]}
        />
      </ReadingSection>
    )
  }
  if (m.capability === 'customer_answer') {
    const outcome = answerOutcome(m.result?.returnStatus)
    const outcomeLine = (
      <Hint data-testid="action-answer-outcome">{`${outcome.charAt(0).toUpperCase()}${outcome.slice(1)}.`}</Hint>
    )
    if (m.redacted) {
      return (
        <ReadingSection label="Your answer">
          <Hint>The answer was removed at the customer’s request.</Hint>
          {outcomeLine}
        </ReadingSection>
      )
    }
    const after = isAnswerAfter(m.after) ? m.after : null
    if (!after) return null
    return (
      <ReadingSection label="Your answer">
        <Card tone="recessed" padding="sm">
          <MessageBody data-testid="action-answer">
            {after.answerText}
          </MessageBody>
        </Card>
        {outcomeLine}
      </ReadingSection>
    )
  }
  return null
}

/** What an erased decision's drawer says in place of what was said (MAR-252). */
export const ERASED_COPY = 'This customer asked to be forgotten — what was said is gone.'
/** The sentinel erasure writes over a decision's text — an older feed still sends it raw. */
const ERASED_SENTINEL = '[REDACTED]'

/**
 * Whether this entry's customer was erased (MAR-252). The feed says so (`erased`); an older feed
 * sent the sentinel itself in `message` / `reasoning`, and the drawer must never print it as
 * what she sent or what she thought.
 */
export function isErasedEntry(a: Pick<Activity, 'erased' | 'reasoning' | 'message'> | null | undefined): boolean {
  if (!a) return false
  return a.erased === true || a.reasoning === ERASED_SENTINEL || a.message === ERASED_SENTINEL
}

/** A conversation join that could not be READ — distinct from "there was none". */
const CONVERSATION_UNAVAILABLE = 'She couldn’t load the conversation for this one.'
/** The ledger page spent its lookup budget before this line: NOT looked up, which is not missing. */
const CONVERSATION_SKIPPED = 'She didn’t look up the conversation for this one.'

/**
 * Whether to say the conversation was not looked up (`enrichmentSkipped`, ledger rows only).
 * Reads `enrichmentSkipped` ALONE: the name cap is its own flag (`nameSkipped`), and a name not
 * looked up is already said by the line itself ("a customer") — it is never this copy.
 * Only where there is no conversation summary to show, and not while the full entry is being
 * read — the detail is about to answer for itself.
 */
export function conversationSkipped(
  a: Pick<Activity, 'enrichmentSkipped'> & { conversation?: { summary?: string | null } | null },
  loading?: boolean
): boolean {
  return a.enrichmentSkipped === true && !a.conversation?.summary?.trim() && !loading
}

/** A Ledger campaign line's detail (`grp_…`, MAR-246) — its members, not one judgment. */
export function isLedgerCampaign(a: unknown): a is LedgerCampaignDetail {
  return !!a && typeof a === 'object' && (a as { source?: unknown }).source === 'campaign' &&
    Array.isArray((a as { decisions?: unknown }).decisions)
}

/** The plain noun for a campaign trigger — never the raw token (MAR-246). */
export const CAMPAIGN_TRIGGER_NOUNS: Record<string, string> = {
  inspiration: 'new-season picks',
  back_in_stock: 'restock',
  price_drop: 'price drop',
  browse_timeout: 'browse follow-up',
}

/** A member's outcome chip: the server's class chip, or — for an acted one, which has none — the send. */
function memberChip(m: LedgerCampaignMember): { label: string; tone: ChipTone } | null {
  if (m.chip) return { label: m.chip, tone: isGoldChip(m.chip) ? 'gold' : 'neutral' }
  if (m.receipt?.class === 'help_executed') return { label: 'Sent', tone: 'sage' }
  if (m.receipt?.class === 'help_in_flight') return { label: 'On its way', tone: 'sage' }
  return null
}

/**
 * "The decisions, counted" — the campaign's stat grid, from the Journal rollup's counts. Only
 * non-zero counts are shown; a wait and a hold are never one number.
 */
function campaignStats(c: LedgerCampaignDetail): { value: string; label: string }[] {
  const m = c.metadata || ({} as LedgerCampaignDetail['metadata'])
  const by = m.byClass || {}
  // People, not decisions: the headline's own count, so "3 customers" never sits beside "4 customers".
  // A greeting rollup (MAR-387) counts visitors who were not greeted, not customers reached.
  const noun = c.rollup === 'greeting_wait' ? ['visitor', 'visitors'] : ['customer', 'customers']
  const stats: { value: string; label: string }[] = [{ value: String(c.people), label: c.people === 1 ? noun[0] : noun[1] }]
  const add = (n: number | undefined, label: string) => {
    if (typeof n === 'number' && n > 0) stats.push({ value: String(n), label })
  }
  // A shopper decided on more than once: say how many decisions that was, under its own word.
  if (c.turns !== c.people) add(c.turns, c.turns === 1 ? 'decision' : 'decisions')
  add(m.totalSent, 'sent')
  add(m.totalSending, 'on their way')
  add(m.totalScheduled, 'waiting to send')
  add(m.totalHeldBack, 'held by a limit')
  add(m.totalAwaitingApproval, 'waiting on you')
  add(by.escalate, 'asked you')
  add(by.help_shadowed, 'held in shadow')
  add(by.wait, 'she chose not to')
  add(by.execution_failed, 'didn’t go through')
  add(m.totalOpened, 'opened')
  add(m.totalClicked, 'clicked')
  if (typeof m.totalRevenue === 'number' && m.totalRevenue > 0) stats.push({ value: fmtCurrency(m.totalRevenue), label: 'attributed' })
  return stats
}

/**
 * A member she CHOSE not to act for — her own `wait`, filed `chose_not`. Never a limit's hold, a
 * shadow, a failure or a question to the merchant: those are not her judgment, and asking her
 * "why did you hold back" about one would put words in her mouth.
 */
function isChoseNotMember(m: LedgerCampaignMember): boolean {
  return m.filter === 'chose_not'
}

/**
 * The ONE decision the Ask dock is pinned to for "Ask her about one she chose not to" (MAR-316):
 * the newest member she chose not to act for. A campaign line (`grp_…`) is not itself a record
 * Ask can prove belongs to this workspace — only a decision is — and merchant-chat proves and
 * shows the model exactly that one decision, so the pin and the question name ONE decision, never
 * the whole line.
 */
export function campaignSkipAnchor(c: LedgerCampaignDetail): string | null {
  return c.decisions.find(isChoseNotMember)?.id ?? null
}

/** A campaign line's drawer body (MAR-246): the counts, then each person — a door to their decision. */
function CampaignBody({
  c,
  onOpenActivity,
  onAskAboutSkips,
}: {
  c: LedgerCampaignDetail
  onOpenActivity?: (id: string) => void
  onAskAboutSkips?: () => void
}) {
  return (
    <>
      {onAskAboutSkips && campaignSkipAnchor(c) && (
        <div>
          <Button variant="ghost" size="sm" onClick={onAskAboutSkips} data-testid="campaign-ask-skips">
            Ask her about one she chose not to
          </Button>
        </div>
      )}
      {c.titleSource === 'composed' && (
        <Hint data-testid="campaign-composed">
          She didn’t write this title — the Ledger composed it from the{' '}
          {c.decisions.length === 1 ? 'line' : `${c.decisions.length} lines`} below, and each opens on its own.
        </Hint>
      )}
      <ReadingSection label="The decisions, counted">
        <TileGrid size="sm" data-testid="campaign-stats">
          {campaignStats(c).map((st) => (
            <Card key={st.label} padding="sm">
              <Stat value={st.value} label={st.label} />
            </Card>
          ))}
        </TileGrid>
        {c.partial && c.rollup ? (
          <Hint data-testid="campaign-rollup-partial">
            {typeof c.listed === 'number' && c.listed !== c.decisions.length
              ? `The Ledger folded ${c.listed} lines here; reading those minutes again found ${c.decisions.length}.`
              : 'These minutes have changed since the Ledger read them, so this may not be exactly the set it folded.'}{' '}
            Each still opens on its own.
          </Hint>
        ) : (
          c.partial &&
          (c.decisions.length > 0 ? (
            <Hint>There were more that night than she could read at once — these are the first {c.decisions.length}.</Hint>
          ) : (
            <Hint>
              That night was too busy to read in one go, so she couldn&apos;t reach these people just now. Try again in a
              moment.
            </Hint>
          ))
        )}
      </ReadingSection>
      <EntryList label="The people">
        {c.decisions.map((m) => {
          const chip = memberChip(m)
          // One row, one button (Enter and Space for free).
          return (
            <EntryRow
              key={m.id}
              testId="campaign-person"
              onOpen={() => onOpenActivity?.(m.id)}
              title={<span data-testid="campaign-person-name">{m.name || 'A customer'}</span>}
              tag={m.timestamp ? <Meta>{fmtTime(m.timestamp)}</Meta> : undefined}
              why={m.why || m.headline}
              chips={
                chip || (m.turns && m.turns > 1) ? (
                  <>
                    {chip && <Chip tone={chip.tone}>{chip.label}</Chip>}
                    {m.turns && m.turns > 1 ? <Chip data-testid="campaign-person-turns">{m.turns} replies</Chip> : null}
                  </>
                ) : undefined
              }
            />
          )
        })}
      </EntryList>
    </>
  )
}

/** What she was looking at — the facts behind a Ledger decision, as mono key/value pairs. */
function ledgerFacts(a: DisplayActivity): [string, string][] {
  const out: [string, string][] = []
  const add = (k: string, v: unknown) => {
    if (v === null || v === undefined || v === '') return
    out.push([k, String(v)])
  }
  add('trigger', a.trigger)
  add('channel', channelLabel(a.channel))
  add('timing', a.timing)
  add('cartValue', typeof a.cartValue === 'number' ? fmtCurrency(a.cartValue) : a.cartValue)
  add('product', a.productTitle)
  add('items', a.itemCount)
  add('signal', a.signalType)
  add('status', a.status)
  add('entry', `${a.type || 'decision'} · ${a.id}`)
  return out
}

type TranscriptTurn = NonNullable<NonNullable<Activity['content']>['transcript']>[number]

/** The exchange, turn by turn — the decision drawer's and the reply drawer's one rendering. */
function Exchange({ turns }: { turns: TranscriptTurn[] }) {
  return (
    <Transcript
      label="The exchange"
      turns={turns.map((m) =>
        m.role === 'system'
          ? { from: 'system' as const, text: m.content }
          : {
              from: m.role === 'assistant' ? ('marva' as const) : ('customer' as const),
              text: m.content,
              meta: `${m.role === 'assistant' ? 'Marva' : 'Customer'}${m.timestamp ? ` · ${fmtTime(m.timestamp)}` : ''}`,
            }
      )}
    />
  )
}

/** A reading drawer's "Reading the full entry…" while the detail behind a row is read. */
function ReadingEntry() {
  return <LoadingState caption="Reading the full entry…" />
}

/**
 * A reply's drawer body (MAR-110). A reply is not a judgment: no "why", no feedback, no limits —
 * which channel, what happened to the send, and the conversation it belongs to.
 */
function ReplyBody({ r, loading }: { r: LedgerReplyDetail; loading?: boolean }) {
  const channel = channelLabel(r.channel)
  const stateWords = r.sendState ? REPLY_SEND_STATE_WORDS[r.sendState] : null
  const times = sentTimes(r.sendCount)
  const turns = r.transcript?.filter((m) => m.content) ?? []
  const summary = r.conversation?.summary?.trim()
  const missing = new Set<string>(r.enrichmentUnavailable ?? [])
  const out = r.outbound
  const showSkipped = conversationSkipped(r, loading) && turns.length === 0
  const events = out
    ? [
        // "Sent" only when no chip above already says it (MAR-412).
        out.sentAt ? { when: fmtStamp(out.sentAt), datetime: out.sentAt, title: r.chip === 'Sent' ? undefined : 'Sent' } : null,
        out.status ? { when: '', title: `Carrier status · ${out.status}` } : null,
        out.errorCode ? { when: '', title: `Error code · ${out.errorCode}` } : null,
      ].filter((e): e is NonNullable<typeof e> => !!e)
    : []
  return (
    <>
      <ReadingSection label="What happened">
        <ChipGroup>
          {channel && <Chip>{channel}</Chip>}
          {r.chip && <Chip tone={isGoldChip(r.chip) ? 'gold' : 'neutral'}>{r.chip}</Chip>}
          {(r.turns ?? 1) > 1 && <Chip>{r.turns} replies</Chip>}
        </ChipGroup>
        {(stateWords || times) && (
          <p className="mv-prose">
            {stateWords}
            {times && ` It was ${times}.`}
          </p>
        )}
        {events.length > 0 && <Timeline items={events} label="The send" />}
        {missing.has('outbound') && <Hint>She couldn’t load the carrier’s status for this one.</Hint>}
      </ReadingSection>

      {summary && (
        <ReadingSection label="The conversation">
          <Card tone="recessed" padding="sm">
            <p className="mv-prose">{summary}</p>
          </Card>
        </ReadingSection>
      )}
      {turns.length > 0 && (
        <ReadingSection label="The exchange">
          <Exchange turns={turns} />
        </ReadingSection>
      )}
      {(missing.has('conversation') || missing.has('message') || missing.has('transcript')) && (
        <Hint>{CONVERSATION_UNAVAILABLE}</Hint>
      )}
      {showSkipped && <Hint>{CONVERSATION_SKIPPED}</Hint>}
    </>
  )
}

/** A campaign member row (an older aggregated entry's people): a door to that person's decision. */
function PersonCard({
  id,
  chip,
  timestamp,
  reason,
  onOpenActivity,
}: {
  id: string
  chip: { label: string; tone: ChipTone }
  timestamp?: string | null
  reason?: ReactNode
  onOpenActivity?: (id: string) => void
}) {
  return (
    <EntryRow
      onOpen={() => onOpenActivity?.(id)}
      title={<Chip tone={chip.tone}>{chip.label}</Chip>}
      tag={timestamp ? <Meta>{fmtTime(timestamp)}</Meta> : undefined}
      why={reason || undefined}
      clamp={false}
    />
  )
}

/** What an open drawer with no entry shows: the Ledger's deep link being read, missing or failed. */
export type StoryPlaceholder = { eyebrow: ReactNode; title: ReactNode; body?: ReactNode }

export default function StoryDrawer({
  activity,
  loading,
  feedback,
  onClose,
  onExited,
  onFeedback,
  onUndoFeedback,
  onAskAboutSkips,
  onOpenActivity,
  onBack,
  title,
  placeholder,
  extra,
  scope,
}: {
  activity: DisplayActivity | null
  loading?: boolean
  /** This entry's answer to "Was this the right call?", as the caller holds it. */
  feedback?: FeedbackState
  onClose: () => void
  /** The Drawer's slide-out has finished (`useDrawerPresence`). */
  onExited?: () => void
  onFeedback: (payload: ActivityFeedback) => void
  /** Drops a settled answer still inside its undo window (before it is sent). */
  onUndoFeedback?: () => void
  /** A campaign line: open Ask pinned to it, about the people she did not act for. */
  onAskAboutSkips?: () => void
  onOpenActivity?: (id: string) => void
  onBack?: () => void
  /**
   * The headline to show instead of `storyTitle(activity)` — the Ledger titles a decision by its
   * one-line receipt headline, so the drawer names the line the merchant clicked. A caller that
   * pins the Ask entity must pin this same string.
   */
  title?: string
  /**
   * Shown in an OPEN drawer that has no entry to render — the Ledger's deep link while the
   * decision is being read, or when it cannot be found. Absent, a null `activity` is a closed
   * drawer.
   */
  placeholder?: StoryPlaceholder
  /** Extra content above the feedback section (the Ledger's link to Authority). */
  extra?: ReactNode
  /** The workspace the entry belongs to: a close caused by a switch drops it at once. */
  scope?: string
}) {
  // Which entry the facts toggle is open for — scoped at read, so a new entry opens closed.
  const [factsFor, setFactsFor] = useState<string | null>(null)
  const factsId = useId()

  // The v6 Reading drawer (MAR-246, D-36): eyebrow "Decision", the line's title, the time, then
  // Marva's one-sentence WHY first (never the raw reasoning in its place), and a toggle that
  // reveals the facts she was looking at AND her full working. A campaign line (`grp_…`) gets
  // "The decisions, counted" and its people.
  const campaign = isLedgerCampaign(activity) ? (activity as unknown as LedgerCampaignDetail) : null
  // The merchant's own applied change (MAR-175): its own body — no why, no feedback.
  const action = isMerchantAction(activity) ? (activity as unknown as Partial<LedgerMerchantActionDetail>) : null
  const a = campaign || action ? null : activity
  // A reply record (MAR-110) gets its own body: no why, no feedback — it is not a judgment.
  const reply = isReplyDetail(a) ? a : null
  const open = !!a || !!campaign || !!action || placeholder != null
  const when = a ? fmtWhen(a.timestamp) : ''
  const c = a?.content
  // Dedupe: each distinct piece of text appears once (title vs description vs
  // reasoning can collide in the data).
  const seen = new Set<string>()
  const uniq = (t?: string) => {
    const k = t?.trim()
    if (!k || seen.has(k)) return undefined
    seen.add(k)
    return t
  }
  const isAggregated = !!a?.metadata?.isAggregated
  const titleText = a ? title || storyTitle(a) : ''
  if (titleText) seen.add(titleText.trim())
  // The Ledger leads with the line's one sentence (`why`): Marva's own, or the server's fallback
  // from her recorded judgment (`fallbackLedgerWhy`, MAR-269) — the same sentence as the row. When
  // the server sends none, it shows no why at all — the raw reasoning is her working, behind the
  // toggle, never promoted to the why. `visibleReasoning`, never `a.reasoning`: a decision
  // failure's reasoning is its error text, and an older feed still sends it raw.
  // Erased (MAR-252): nothing she said or thought about this person is shown — not the
  // sentinel, not an empty box — only the honest line saying why.
  const erased = isErasedEntry(a)
  const ledgerWhy = a && !erased ? (a as DisplayActivity & { why?: string | null }).why?.trim() || undefined : undefined
  const working = a && !erased ? visibleReasoning(a) : undefined
  const factsOpen = !!a && factsFor === a.id
  if (ledgerWhy) seen.add(ledgerWhy)
  // The composed "what she did" is not shown, but it still claims its text: a message body that
  // merely repeats it is deduped away below.
  const rawLead = a ? narrative(a) : null
  if (typeof rawLead === 'string') uniq(rawLead)
  const transcript = erased ? undefined : c?.transcript?.filter((m) => m.content)
  const missing = new Set<string>(a?.enrichmentUnavailable ?? [])
  const convUnavailable = missing.has('transcript') || missing.has('conversation')
  const convSkipped =
    !!a && conversationSkipped(a as Activity & { conversation?: { summary?: string | null } | null }, loading)
  // No campaign-level "What I sent": every send is personalized per person, so
  // one recipient's message would masquerade as the campaign's. The per-person
  // rows below drill into each individual entry for the actual content.
  const messageText = isAggregated || erased ? undefined : uniq(c?.bodyText || c?.message || a?.message)
  const dcode = a ? a.mergedCode || a.metadata?.code : undefined
  const dpct = a ? a.mergedPercent ?? a.metadata?.discountPercent : undefined
  const st = a ? actionState(a) : undefined
  // Held/shadow decisions never went out — say so honestly, and a decision still in flight
  // ('dispatched', 'scheduled', 'pending_delivery') gets its own tense rather than either
  // claim. The status vocabulary lives with the pill so the two can never disagree.
  const contentCaption = a ? contentLabel(a) : 'What I sent'
  // Aggregated campaign detail: per-person decisions + send/open/click timeline.
  // Split by RECEIPT CLASS, not by the legacy engaged/skipped sides: an `engage` may be a help
  // your rules withheld or one that failed, and a `skip` may be a model failure. "Chose to wait"
  // (her call) and "Held back by your rules" (yours) are different receipts and never share a
  // heading.
  const engagedAll = isAggregated ? a?.decisions?.engaged || [] : []
  const skippedAll = isAggregated ? a?.decisions?.skipped || [] : []
  const engagedClass = (d: CampaignDecisionRow) => campaignRowReceipt(d, 'engaged').class
  const sentRows = engagedAll.filter((d) => ['help_executed', 'help_in_flight'].includes(engagedClass(d)))
  const pendingApproval = (d: CampaignDecisionRow) => campaignRowReceipt(d, 'engaged').pendingApproval
  const heldRows = engagedAll.filter((d) => engagedClass(d) === 'help_blocked' && !pendingApproval(d))
  const approvalRows = engagedAll.filter((d) => engagedClass(d) === 'help_blocked' && pendingApproval(d))
  const plannedRows = engagedAll.filter((d) => !sentRows.includes(d) && !heldRows.includes(d) && !approvalRows.includes(d))
  const waitedRows = skippedAll.filter((d) => campaignRowReceipt(d, 'skipped').class === 'wait')
  const otherSkippedRows = skippedAll.filter((d) => !waitedRows.includes(d))
  // The agent's reasoning is shown only where the shared rule allows — a failure's `reasoning`
  // holds error text, and a legacy skip's cannot be told apart from one.
  const judgedReasoning = campaignRowReasoning
  const byClass = a?.metadata?.byClass
  // Held back by a rule vs waiting on the merchant's approval: two different receipts.
  const heldBackCount = a?.metadata?.totalHeldBack || 0
  const awaitingCount = a?.metadata?.totalAwaitingApproval || 0
  // A send that broke vs a decision never reached: the second never attempted a send.
  const executionFailedCount = byClass?.execution_failed || 0
  const undecidedCount = byClass?.decision_failed || 0
  const timeline = (a?.events || [])
    .filter((e) => TIMELINE_EVENTS.has(e.eventType) && e.timestamp)
    .sort((x, y) => new Date(x.timestamp!).getTime() - new Date(y.timestamp!).getTime())
  // LLM-timed sends carry an ISO timestamp in `timing` — a "Scheduled" chip
  // must answer "for when?", and an expired one "when was it meant to go?".
  // Legacy tokens (now / delay_1h) don't parse and render nothing.
  const timingMs = a?.timing ? new Date(a.timing).getTime() : NaN
  const schedulePrefix = a ? scheduleLinePrefix(a) : null
  const scheduledLine =
    !isNaN(timingMs) && schedulePrefix
      ? `${schedulePrefix} ${fmtWhen(new Date(timingMs).toISOString())}`
      : null
  const events = [
    ...(scheduledLine ? [{ when: '', title: scheduledLine }] : []),
    ...timeline.map((e) => {
      const label = outcomeLabel(e.eventType)
      return {
        when: fmtStamp(e.timestamp!),
        datetime: e.timestamp!,
        // The Outcome chip above already names this event ("Sent"): the row is then just its when,
        // rather than the chip's word again (MAR-412).
        title: !isAggregated && label === st?.label ? undefined : label,
      }
    }),
  ]

  const back = onBack ? (
    <div>
      <Button variant="ghost" size="sm" icon={<Icon name="left" size={13} />} onClick={onBack}>
        Back to the campaign
      </Button>
    </div>
  ) : null

  // The header: what the drawer is titled, its kicker and its when-line.
  let eyebrow: ReactNode = null
  let heading: ReactNode = ''
  let sub: ReactNode = null
  let body: ReactNode = null

  if (campaign) {
    // A short-cluster rollup (MAR-387) is not a campaign she ran: it is several lines read as one.
    eyebrow = campaign.rollup ? 'Rolled up' : 'Campaign'
    heading = title || campaign.headline
    sub = campaign.timestamp ? fmtWhen(campaign.timestamp) : null
    body = (
      <>
        {loading && <ReadingEntry />}
        <CampaignBody c={campaign} onOpenActivity={onOpenActivity} onAskAboutSkips={onAskAboutSkips} />
      </>
    )
  } else if (action) {
    eyebrow = 'You changed'
    heading = title || action.headline || 'Your change'
    sub = action.appliedAt || action.timestamp ? fmtWhen((action.appliedAt || action.timestamp)!) : null
    body = (
      <>
        {loading && <ReadingEntry />}
        <MerchantActionBody m={action} />
      </>
    )
  } else if (reply) {
    eyebrow = 'Reply'
    heading = titleText || 'Reply'
    sub = when
    body = (
      <>
        {loading && <ReadingEntry />}
        <ReplyBody r={reply} loading={loading} />
        {extra}
      </>
    )
  } else if (a) {
    eyebrow = 'Decision'
    heading = titleText || 'Decision'
    sub = when
    const sendRows = (
      rows: CampaignDecisionRow[],
      side: 'engaged' | 'skipped',
      reason: (d: CampaignDecisionRow) => ReactNode,
      chip?: (d: CampaignDecisionRow) => { label: string; tone: ChipTone }
    ) => (
      <EntryList>
        {rows.map((d) => (
          <PersonCard
            key={d.id}
            id={d.id}
            chip={chip ? chip(d) : campaignRowPill(d, side)}
            timestamp={d.timestamp}
            reason={reason(d)}
            onOpenActivity={onOpenActivity}
          />
        ))}
      </EntryList>
    )
    body = (
      <>
        {back}
        {ledgerWhy && (
          <p className="mv-prose" data-testid="drawer-why">
            {ledgerWhy}
          </p>
        )}
        <div>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={factsOpen}
            aria-controls={factsId}
            onClick={() => setFactsFor(factsOpen ? null : a.id)}
          >
            {factsOpen ? 'Hide what she was looking at' : 'Show what she was looking at'}
          </Button>
        </div>
        <div id={factsId}>
          {factsOpen && (
            <Reading>
              <ReadingSection label="What she saw" testId="drawer-facts">
                <KeyValue mono items={ledgerFacts(a).map(([k, v]) => ({ label: k, value: v }))} />
              </ReadingSection>
              {working && (
                <ReadingSection label="Her working">
                  <MessageBody data-testid="drawer-working">
                    {working}
                  </MessageBody>
                </ReadingSection>
              )}
            </Reading>
          )}
        </div>

        {loading && <ReadingEntry />}

        {erased ? (
          <Hint data-testid="erased-note">{ERASED_COPY}</Hint>
        ) : transcript && transcript.length > 0 ? (
          // The Ledger's chat line shows the conversation itself (MAR-252, v6 "The exchange").
          <ReadingSection label="The exchange">
            <Exchange turns={transcript} />
          </ReadingSection>
        ) : messageText ? (
          <ReadingSection label={contentCaption}>
            <Card tone="recessed" padding="sm">
              <MessageBody>{messageText}</MessageBody>
            </Card>
          </ReadingSection>
        ) : null}
        {!erased && !(transcript && transcript.length > 0) && convUnavailable && <Hint>{CONVERSATION_UNAVAILABLE}</Hint>}
        {!erased && !(transcript && transcript.length > 0) && !convUnavailable && convSkipped && (
          <Hint>{CONVERSATION_SKIPPED}</Hint>
        )}

        {sentRows.length > 0 && (
          <ReadingSection label="Who I sent it to">{sendRows(sentRows, 'engaged', (d) => judgedReasoning(d, 'engaged'))}</ReadingSection>
        )}
        {approvalRows.length > 0 && (
          <ReadingSection label="Waiting on your approval">
            {sendRows(approvalRows, 'engaged', (d) => judgedReasoning(d, 'engaged'))}
          </ReadingSection>
        )}
        {heldRows.length > 0 && (
          <ReadingSection label="Held back by your rules">
            {sendRows(heldRows, 'engaged', (d) => {
              const hold = campaignRowHold(d)
              return hold ? `Wanted to send it, but ${hold}.` : undefined
            })}
          </ReadingSection>
        )}
        {plannedRows.length > 0 && (
          <ReadingSection label="Planned, not sent yet — or didn’t go through">
            {sendRows(plannedRows, 'engaged', (d) => judgedReasoning(d, 'engaged'))}
          </ReadingSection>
        )}
        {waitedRows.length > 0 && (
          <ReadingSection label="Chose to wait — and why">
            {sendRows(
              waitedRows,
              'skipped',
              (d) => judgedReasoning(d, 'skipped'),
              (d) => ({ label: skipReasonLabel(d.skipReason), tone: 'sage' })
            )}
          </ReadingSection>
        )}
        {otherSkippedRows.length > 0 && (
          <ReadingSection label="Didn’t send">
            {sendRows(
              otherSkippedRows,
              'skipped',
              (d) => judgedReasoning(d, 'skipped'),
              (d) => ({ label: skipReasonLabel(d.skipReason), tone: 'neutral' })
            )}
          </ReadingSection>
        )}

        <ReadingSection label="Outcome">
          <ChipGroup>
            {isAggregated ? (
              <>
                <Chip>{a.metadata?.totalSent || 0} sent</Chip>
                {/* Handed off, nothing back yet. Folding these into "sent" would claim
                    confirmations we do not have; dropping them under-reported the campaign. */}
                {(a.metadata?.totalSending || 0) > 0 && <Chip>{a.metadata!.totalSending} sending</Chip>}
                {heldBackCount > 0 && <Chip tone="sage">{heldBackCount} held back</Chip>}
                {(a.metadata?.totalOpened || 0) > 0 && <Chip tone="sage">{a.metadata!.totalOpened} opened</Chip>}
                {(a.metadata?.totalClicked || 0) > 0 && <Chip tone="sage">{a.metadata!.totalClicked} clicked</Chip>}
                {/* By class: her waits, and faults, are never one "held off" count. An older
                    feed without byClass gets the honest legacy word instead. */}
                {byClass
                  ? (byClass.wait || 0) > 0 && <Chip tone="sage">waited on {byClass.wait}</Chip>
                  : (a.metadata?.totalSkipped || 0) > 0 && <Chip>{a.metadata!.totalSkipped} not sent</Chip>}
                {awaitingCount > 0 && <Chip tone="sage">{awaitingCount} waiting on your approval</Chip>}
                {executionFailedCount > 0 && <Chip tone="gold">{executionFailedCount} didn’t go through</Chip>}
                {undecidedCount > 0 && <Chip tone="gold">{undecidedCount} undecided — a fault on our side</Chip>}
                {(a.metadata?.totalRevenue || 0) > 0 && <Chip tone="gold">{fmtCurrency(a.metadata!.totalRevenue!)}</Chip>}
              </>
            ) : (
              <>
                {a.revenue != null && <Chip tone="gold">{fmtCurrency(a.revenue)}</Chip>}
                {dcode && (
                  <Chip tone="gold">
                    {dpct ? `${dpct}% · ` : ''}
                    {dcode}
                  </Chip>
                )}
                {st && <Chip tone={st.tone}>{st.label}</Chip>}
                {!st && a.revenue == null && !dcode && <Chip>Logged</Chip>}
              </>
            )}
          </ChipGroup>
          {events.length > 0 && <Timeline items={events} label="What happened" />}
        </ReadingSection>

        {extra}

        <DecisionFeedback
          entryId={a.id}
          state={feedback}
          channelLabel={channelLabel}
          onRecord={onFeedback}
          onUndo={onUndoFeedback}
        />
      </>
    )
  } else if (placeholder) {
    eyebrow = placeholder.eyebrow
    heading = placeholder.title
    // A person still being read (or not found, or failed) has a way back all the same.
    body = (
      <>
        {back}
        {placeholder.body}
      </>
    )
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      size="lg"
      modal={false}
      scope={scope}
      eyebrow={eyebrow}
      title={heading}
      subtitle={sub ? <Meta>{sub}</Meta> : undefined}
    >
      <Reading>{body}</Reading>
    </Drawer>
  )
}
